/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Go SDK", description = "Hightouch's Go SDK for collecting events", lastUpdatedDate = new Date(1710201600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Go SDK",
        description: "Hightouch's Go SDK for collecting events",
        lastUpdatedDate: "Tue Mar 12 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Page",
          "depth": 1,
          "id": "page"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Close",
          "depth": 1,
          "id": "close"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span",
      h3: "h3",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Go SDK makes it easy to track events from Go applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Go SDK can be installed from ", _jsx(_components.a, {
          href: "https://github.com/ht-sdks/events-sdk-go",
          children: "GitHub"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-go",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "go"
          }), " get github.com/ht-sdks/events-sdk-", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "go"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To initialize the Go SDK in your application, create a new ", _jsx(_components.code, {
          children: "Client"
        }), " instance:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-go",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "package"
          }), " main\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "import"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"github.com/ht-sdks/events-sdk-go\""
          }), "\n)\n\n", _jsxs(_components.span, {
            className: "hljs-function",
            children: [_jsx(_components.span, {
              className: "hljs-keyword",
              children: "func"
            }), " ", _jsx(_components.span, {
              className: "hljs-title",
              children: "main"
            }), _jsx(_components.span, {
              className: "hljs-params",
              children: "()"
            })]
          }), " {\n  client, _ := htevents.NewWithConfig(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"WRITE_KEY\""
          }), ", htevents.Config{\n    Endpoint: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://us-east-1.hightouch-events.com\""
          }), "\n  })\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "defer"
          }), " client.Close()\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "htevents.Identify"
        }), " message sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: ["Unlike the ", _jsx(_components.a, {
          href: "./browser",
          children: "Browser SDK"
        }), ", it does ", _jsx(_components.strong, {
          children: "not"
        }), " persist the user ID and\ntraits locally, so user IDs must be explicitly added to other events. This is\nbecause server side events are usually servicing many different users at once."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "Enqueue"
          }), "(htevents.Identify{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "UserId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Traits"
          }), ": htevents.Traits{\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"location\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "UserId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AnonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Traits"
              })
            }), _jsx(_components.td, {
              children: "Traits"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Context"
              })
            }), _jsx(_components.td, {
              children: "Context"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "Context"
              }), ". By default, ", _jsx(_components.code, {
                children: "Context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "htevents.Track"
        }), " message sends a ", _jsx(_components.code, {
          children: "track"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "Enqueue"
          }), "(htevents.Track{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Event"
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Order completed\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "UserId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Properties"
          }), ": htevents.Properties{\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"total\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "UserId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AnonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Event"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Properties"
              })
            }), _jsx(_components.td, {
              children: "Properties"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Context"
              })
            }), _jsx(_components.td, {
              children: "Context"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "Context"
              }), ". By default, ", _jsx(_components.code, {
                children: "Context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "page",
        children: "Page"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "htevents.Page"
        }), " message sends a ", _jsx(_components.code, {
          children: "page"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "Enqueue"
          }), "(htevents.Page{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Name"
          }), ":   ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "UserId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Properties"
          }), ": htevents.Properties{\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"total\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "UserId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AnonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Properties"
              })
            }), _jsx(_components.td, {
              children: "Properties"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Context"
              })
            }), _jsx(_components.td, {
              children: "Context"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "Context"
              }), ". By default, ", _jsx(_components.code, {
                children: "Context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "htevents.Screen"
        }), " message sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "Enqueue"
          }), "(htevents.Screen{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Name"
          }), ":   ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Getting started\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "UserId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Properties"
          }), ": htevents.Properties{\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"total\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-number",
            children: "29.99"
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "UserId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AnonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Name"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The page's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Properties"
              })
            }), _jsx(_components.td, {
              children: "Properties"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "url"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Context"
              })
            }), _jsx(_components.td, {
              children: "Context"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "Context"
              }), ". By default, ", _jsx(_components.code, {
                children: "Context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "htevents.Group"
        }), " message sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-php",
          children: ["client.", _jsx(_components.span, {
            className: "hljs-title function_ invoke__",
            children: "Enqueue"
          }), "(htevents.Group{\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "GroupId"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"G-1\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "UserId"
          }), ":  ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), ",\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "Traits"
          }), ": htevents.Traits{\n    ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"company_location\""
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"San Francisco\""
          }), ",\n  },\n})\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "UserId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "AnonymousId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The user's anonymous ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "GroupId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Traits"
              })
            }), _jsx(_components.td, {
              children: "Traits"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Context"
              })
            }), _jsx(_components.td, {
              children: "Context"
            }), _jsxs(_components.td, {
              children: ["Overrides to values in the event ", _jsx(_components.code, {
                children: "Context"
              }), ". By default, ", _jsx(_components.code, {
                children: "Context"
              }), " contains information autocollected by the SDK."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "Timestamp"
              })
            }), _jsx(_components.td, {
              children: "Time"
            }), _jsx(_components.td, {
              children: "The date of the message. When backfilling data, this can be set to a date in the past. By default, this is autoset to the current date."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "close",
        children: "Close"
      }), "\n", _jsx(_components.p, {
        children: "The Go SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "Close"
        }), "\nmethod. ", _jsx(_components.code, {
          children: "Close"
        }), " should be called when shutting down your app to make sure no events are lost."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Example usage"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Close"
          }), "()\n"]
        })
      })]
    });
  }
}
export default MDXContent;
